// Dependecies
import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Alert from 'react-bootstrap/Alert';

// Components
import Template from '../components/Template';
import PasswordChangeInput from '../components/PasswordChangeInput';
import RenderTooltip from '../components/RenderTooltip';
import PopUp from '../components/Modal';
import ModalLeave from '../components/Modal/ModalLeave';

// Hooks
import useCountry from '../hooks/useCountries';
import useCity from '../hooks/useCities';
import useRegister from '../hooks/useRegister';
import useTermsAndConditions from '../hooks/useTermsAndConditions';
import useLanguage from '../hooks/useLanguage';

// Images
import check from '../../sass/images/icono-check.svg';
import alert from '../../sass/images/alert/alert.svg';
import info from '../../sass/images/alert/info.svg';
import infoTooltip from '../../sass/images/alert/icon-tooltip-info.svg';
import errorAlert from '../../sass/images/alert/icon-error-alert.svg';

function Register(props) {
  const { countries } = useCountry();
  const { cities } = useCity();
  const { t } = props;
  const { termAndCondition, authorizationData } = useTermsAndConditions();

  const { control, register, handleSubmit, watch, errors, clearErrors, setError, reset } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldUnregister: true,
  });
  const { locale } = useLanguage();
  const {
    openModal,
    closeModal,
    acceptModal,
    submit,
    validatePassword,
    messageError,
    isBlockedModal,
    setIsBlocked,
  } = useRegister(clearErrors, setError, locale, reset);

  return (
    <Template
      type={2}
      component="register"
      classNameContainer="register"
      className="container_menu background register"
    >
      {openModal ? (
        <PopUp
          open={openModal}
          close={closeModal}
          icon={check}
          title={t('successful')}
          description={t('answer')}
          go={`${t('go')}`}
          accept={acceptModal}
          modalDesign
          btnAccept
          btnClose={false}
          classNameContent="modal-reset register__modal"
          classNameBtn="close_modal"
        />
      ) : (
        ''
      )}

      <ModalLeave
        isBlocked={isBlockedModal}
        title={t('currently-registration')}
        content={t('break-registration')}
        register={t('register')}
        props={props}
      />

      <div className="container_register col-12 col-lg-12 mt-2 hv-center">
        <h2>
          <strong>{t('title')}</strong>
        </h2>
        <p className="title">{t('description')} </p>
        <br />
        <form className="form_content_register" onSubmit={handleSubmit(submit)}>
          <div className="row">
            <div className="col-lg-4 col-md-12 col-sm-12">
              <label
                htmlFor="name"
                className={`col-form-label ${errors.name ? 'error_label' : ''}`}
              >
                *{t('name')}
              </label>
              <div className="custom-file">
                <input
                  className="form-control"
                  type="text"
                  title="Solo caracteres"
                  name="name"
                  id="name"
                  ref={register({ required: true })}
                  onChange={(e) => {
                    setIsBlocked(e.target.value.length > 0);
                  }}
                />
                {errors.name && (
                  <div className="error_input">
                    <p>
                      <img src={alert} alt={t('alt:notification')} />
                      {t('required')}
                    </p>
                  </div>
                )}
              </div>
            </div>
            <div className="col-lg-4 col-md-12 col-sm-12">
              <label
                htmlFor="phone_company"
                className={`col-form-label ${errors.phone_company ? 'error_label' : ''}`}
              >
                *{t('phone')}
              </label>
              <div className="custom-file">
                <input
                  className="form-control"
                  title="Solo números"
                  pattern="^[0-9]+$"
                  type="text"
                  id="phone_company"
                  name="phone_company"
                  ref={register({
                    required: {
                      value: true,
                      message: t('required'),
                    },
                    maxLength: {
                      value: 10,
                      message: t('max-10'),
                    },
                    minLength: {
                      value: 10,
                      message: t('min-10'),
                    },
                    pattern: {
                      value: /[0-9]$/i,
                      message: t('num-'),
                    },
                  })}
                  onChange={(e) => {
                    setIsBlocked(e.target.value.length > 0);
                  }}
                />
                {errors.phone_company && (
                  <div className="error_input">
                    <p>
                      <img src={alert} alt={t('alt:notification')} />
                      {errors.phone_company.message}
                    </p>
                  </div>
                )}
              </div>
            </div>
            <div className="col-lg-4 col-md-12 col-sm-12">
              <label
                htmlFor="rut_company"
                className={`col-form-label ${errors.rut_company ? 'error_label' : ''}`}
              >
                *{t('rut')}
              </label>
              <div className="custom-file">
                <input
                  className="form-control"
                  title="Solo números y - "
                  pattern="^[0-9,-]+$"
                  type="text"
                  id="rut_company"
                  name="rut_company"
                  ref={register({
                    required: {
                      value: true,
                      message: t('required'),
                    },
                    maxLength: {
                      value: 15,
                      message: t('max-15'),
                    },
                    minLength: {
                      value: 8,
                      message: t('min-8'),
                    },
                    pattern: {
                      value: /[0-9,-]$/i,
                      message: t('num-'),
                    },
                  })}
                  onChange={(e) => {
                    setIsBlocked(e.target.value.length > 0);
                  }}
                />
                {errors.rut_company && (
                  <div className="error_input">
                    <p>
                      <img src={alert} alt={t('alt:notification')} />
                      {errors.rut_company.message}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-lg-4 col-md-12 col-sm-12">
              <label
                htmlFor="country"
                className={`col-form-label ${errors.country ? 'error_label' : ''}`}
              >
                *{t('country')}
              </label>
              <div className="custom-file customs-select">
                <div />
                <select
                  defaultValue=""
                  className="form-control"
                  id="country"
                  name="country"
                  ref={register({ required: true })}
                  onChange={(e) => {
                    setIsBlocked(e.target.value.length > 0);
                  }}
                >
                  <option value="" disabled>
                    {t('select')}
                  </option>
                  {countries.map((country) => (
                    <option value={country.id} key={country.id}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>
              {errors.country && (
                <div className="error_input">
                  <p>
                    <img src={alert} alt={t('alt:notification')} />
                    {t('select-country')}
                  </p>
                </div>
              )}
            </div>
            <div className="col-lg-4 col-md-12 col-sm-12">
              <label
                htmlFor="city"
                className={`col-form-label ${errors.city ? 'error_label' : ''}`}
              >
                *{t('city')}
              </label>
              <div className="custom-file customs-select">
                <select
                  defaultValue=""
                  className="form-control"
                  id="city"
                  name="city"
                  ref={register({ required: true })}
                  onChange={(e) => {
                    setIsBlocked(e.target.value.length > 0);
                  }}
                >
                  <option value="" disabled>
                    {t('select')}
                  </option>
                  {cities.map((city) => (
                    <option value={city.id} key={city.id}>
                      {city.name}
                    </option>
                  ))}
                </select>
              </div>
              {errors.country && (
                <div className="error_input">
                  <p>
                    <img src={alert} alt={t('alt:notification')} />
                    {t('select-city')}
                  </p>
                </div>
              )}
            </div>
            <div className="col-lg-4 col-md-12 col-sm-12">
              <label
                htmlFor="email"
                className={`col-form-label ${errors.email ? 'error_label' : ''}`}
              >
                *{t('email')}
              </label>
              <div className="custom-file">
                <input
                  className="form-control"
                  id="email"
                  name="email"
                  ref={register({ required: true, pattern: /^\S+@\S+$/i })}
                  type="email"
                  onChange={(e) => {
                    setIsBlocked(e.target.value.length > 0);
                  }}
                />
                {errors.email && (
                  <div className="error_input">
                    <p>
                      <img src={alert} alt={t('alt:notification')} />
                      {t('required')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-lg-4 col-md-12 col-sm-12">
              <OverlayTrigger
                placement="top"
                delay={{ show: 50, hide: 500 }}
                overlay={(componentProps) =>
                  RenderTooltip({
                    ...componentProps,
                    unique: true,
                    text: t('tooltip'),
                    src: infoTooltip,
                    id: 'overlay-example',
                    classtxt: 'tooltip_txt',
                    classImage: 'tooltip_info',
                    alt: t('alt:tooltip'),
                  })
                }
              >
                <img className="info" src={info} alt={t('alt:tooltip')} />
              </OverlayTrigger>
              <label
                htmlFor="password"
                className={`col-form-label ${errors.password ? 'error_label' : ''}`}
              >
                *{t('password')}
              </label>
              <div className="custom-file">
                <Controller
                  className="form-control"
                  as={<PasswordChangeInput />}
                  defaultValue=""
                  control={control}
                  id="password"
                  name="password"
                  rules={{
                    required: true,
                    validate: (value) => validatePassword(value, watch('c_password'), 'password'),
                  }}
                />
              </div>
              {errors.password && (
                <div className="error_input">
                  <p>
                    <img src={alert} alt={t('alt:notification')} />
                    {t('required')}
                  </p>
                </div>
              )}
            </div>
            <div className="col-lg-4 col-md-12 col-sm-12">
              <label
                htmlFor="c_password"
                className={`col-form-label ${errors.c_password ? 'error_label' : ''}`}
              >
                *{t('confirm-password')}
              </label>
              <div className="custom-file">
                <Controller
                  className="form-control"
                  as={<PasswordChangeInput />}
                  defaultValue=""
                  control={control}
                  id="c_password"
                  name="c_password"
                  rules={{
                    required: true,
                    validate: (value) => validatePassword(value, watch('password'), 'c_password'),
                  }}
                />
              </div>
              {errors.c_password && (
                <div className="error_input">
                  <p>
                    <img src={alert} alt={t('alt:notification')} />
                    {errors.c_password.message && errors.c_password.message === 'Password is empty'
                      ? t('enter-confirm-password')
                      : t('not')}
                  </p>
                </div>
              )}
            </div>
            <div className="col-lg-4 col-md-12 col-sm-12">
              <label htmlFor="adviser" className="col-form-label">
                {t('service')}
              </label>
              <div className="custom-file">
                <input
                  className="form-control"
                  id="adviser"
                  name="adviser"
                  ref={register({
                    maxLength: {
                      value: 200,
                      message: t('max-200'),
                    },
                    minLength: {
                      value: 8,
                      message: t('min-8'),
                    },
                    pattern: {
                      value:
                        /[a-zA-Z0-9_]+([.][a-zA-Z0-9_]+)*@[a-zA-Z0-9_]+([.][a-zA-Z0-9_]+)*[.][a-zA-Z]{1,5}$/i,
                      message: t('validate-email'),
                    },
                  })}
                  placeholder={t('adviser')}
                  onChange={(e) => {
                    setIsBlocked(e.target.value.length > 0);
                  }}
                />
                {errors.adviser && (
                  <div className="error_input">
                    <p>
                      <img src={alert} alt="" />
                      {errors.adviser.message}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col">
              <label htmlFor="tyc" className="col-form-label">
                *{t('field-required')}
              </label>
            </div>
            <div className="col-md-12 col-sm-12">
              {messageError !== '' && messageError !== undefined && (
                <>
                  <Alert variant="danger" className="alert-response">
                    <img src={errorAlert} alt={t('alt:errorAlert')} />
                    <p>
                      {
                        {
                          1: t('alredy-exist'),
                          2: t('nit-exist'),
                          3: t('rut-exist'),
                          4: t('doc-exist'),
                          5: t('error'),
                        }[messageError]
                      }
                    </p>
                  </Alert>
                </>
              )}
            </div>
          </div>
          <div className="row term_conditions">
            <div className="col-lg-4 col-md-3 col-sm-1" />
            <div className="col-lg-6 col-md-7 col-sm-10">
              <div className="box_checkbox">
                <input
                  name="tyc"
                  id="tyc"
                  ref={register({ required: true })}
                  type="checkbox"
                  onChange={(e) => {
                    setIsBlocked(e.target.value.length > 0);
                  }}
                />
                <label htmlFor="tyc" className={errors.tyc ? 'error_label' : ''}>
                  {t('accept')}&nbsp;
                  <strong>
                    <Link
                      target="_blank"
                      className={errors.pdp ? 'error_label' : ''}
                      to={`/${locale}/${t('routers:Terms')}/${termAndCondition.slug}`}
                    >
                      {termAndCondition.title}
                    </Link>
                  </strong>
                </label>
              </div>
            </div>
            <div className="col-lg-2 col-md-2 col-sm-1" />
          </div>
          <div className="row term_conditions">
            <div className="col-lg-4 col-md-3 col-sm-1" />
            <div className="col-lg-6 col-md-7 col-sm-10">
              <div className="box_checkbox">
                <input
                  name="pdp"
                  id="pdp"
                  ref={register({ required: true })}
                  type="checkbox"
                  onChange={(e) => {
                    setIsBlocked(e.target.value.length > 0);
                  }}
                />
                <label htmlFor="pdp" className={errors.pdp ? 'error_label' : ''}>
                  {t('accept-second')}&nbsp;
                  <strong>
                    <Link
                      target="_blank"
                      className={errors.pdp ? 'error_label' : ''}
                      to={`/${locale}/${t('routers:Terms')}/${authorizationData.slug}`}
                    >
                      {authorizationData.title}
                    </Link>
                  </strong>
                </label>
              </div>
            </div>
            <div className="col-lg-2 col-md-2 col-sm-1" />
          </div>
          <div className="row term_conditions">
            <input
              type="submit"
              className="col btn btn-primary send_register register"
              value={t('continue')}
            />
          </div>
        </form>
        <div className="row link_register">
          <label htmlFor="pdp" className="register_link">
            {t('already-registered')}
            <strong>
              <Link to={`/${locale}/${t('routers:Login')}`}>{t('enter-here')}</Link>
            </strong>
          </label>
        </div>
        <br />
      </div>
    </Template>
  );
}

Register.propTypes = {
  t: PropTypes.func.isRequired,
};

export default withTranslation(['register', 'routers', 'alt'])(Register);
